import {
  RoomEvent,
  Track,
  type Participant,
  type Room,
  type TrackPublication,
} from 'livekit-client';
import { MEDIA_TRACK_PREFIX, type ScreenShareLayer, type VideoLayer } from '@liveforge/shared';

/**
 * Binds LiveKit video tracks to `HTMLVideoElement`s the canvas can draw from.
 *
 * `drawImage` needs an element that is actually decoding frames, so every
 * subscribed video track gets one. The elements are never added to the
 * document: they are decode targets, not UI, and attaching them would put a
 * second copy of every feed on screen.
 */

export interface TrackEntry {
  sid: string;
  participantIdentity: string;
  /** `cam-1` for a multicam source, or the publication's own name. */
  trackName: string;
  source: Track.Source;
  element: HTMLVideoElement;
}

export type RegistryListener = (entries: TrackEntry[]) => void;

/** The sources a composition can draw. Audio is Phase 8's problem. */
const DRAWABLE_SOURCES: Track.Source[] = [
  Track.Source.Camera,
  Track.Source.ScreenShare,
  // Where the host's extra multicam sources live - see the Phase 2 decision.
  Track.Source.Unknown,
];

/** A real camera outranks a multicam extra; between equals, the lower name. */
function prefer(candidate: TrackEntry, current: TrackEntry): boolean {
  const candidateIsCamera = candidate.source === Track.Source.Camera;
  const currentIsCamera = current.source === Track.Source.Camera;
  if (candidateIsCamera !== currentIsCamera) {
    return candidateIsCamera;
  }
  return candidate.trackName.localeCompare(current.trackName) < 0;
}

export class TrackRegistry {
  private readonly entries = new Map<string, TrackEntry>();
  private readonly listeners = new Set<RegistryListener>();
  private room: Room | undefined;

  attach(room: Room): void {
    this.detach();
    this.room = room;

    room
      .on(RoomEvent.TrackSubscribed, this.onSubscribed)
      .on(RoomEvent.TrackUnsubscribed, this.onUnsubscribed)
      .on(RoomEvent.LocalTrackPublished, this.onLocalPublished)
      .on(RoomEvent.LocalTrackUnpublished, this.onLocalUnpublished);

    // Whatever was already there before this attached. A registry that only
    // listens for events starts empty and stays empty for anyone who joined
    // before it did.
    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        this.add(publication, participant);
      }
    }
    for (const publication of room.localParticipant.trackPublications.values()) {
      this.add(publication, room.localParticipant);
    }
    this.emit();
  }

  detach(): void {
    this.room
      ?.off(RoomEvent.TrackSubscribed, this.onSubscribed)
      .off(RoomEvent.TrackUnsubscribed, this.onUnsubscribed)
      .off(RoomEvent.LocalTrackPublished, this.onLocalPublished)
      .off(RoomEvent.LocalTrackUnpublished, this.onLocalUnpublished);
    this.room = undefined;

    for (const entry of this.entries.values()) {
      entry.element.srcObject = null;
    }
    this.entries.clear();
  }

  subscribe(listener: RegistryListener): () => void {
    this.listeners.add(listener);
    listener(this.list());
    return () => {
      this.listeners.delete(listener);
    };
  }

  list(): TrackEntry[] {
    return [...this.entries.values()];
  }

  /**
   * One drawable entry per participant, for the AUTO layer.
   *
   * **The order is deliberately not arrival order.** The studio, every guest
   * and Egress each resolve this independently; ordering by when a track
   * happened to arrive would have them disagree, and the director would be
   * framing a picture nobody else is seeing. Identity is the one key every
   * renderer already agrees on.
   *
   * A participant publishing several tracks still gets exactly one tile: their
   * camera if they have one, otherwise their lowest-named extra source, so a
   * host running only multicam angles is not simply absent. Screen shares are
   * excluded - a shared desktop is a source to place deliberately, not a face
   * in a grid of faces.
   */
  tiles(): TrackEntry[] {
    const best = new Map<string, TrackEntry>();

    for (const entry of this.entries.values()) {
      if (entry.source === Track.Source.ScreenShare) {
        continue;
      }
      // A clip the host is playing is published like a source but is not a
      // person, and the automatic grid arranges people.
      if (entry.trackName.startsWith(MEDIA_TRACK_PREFIX)) {
        continue;
      }
      const current = best.get(entry.participantIdentity);
      if (!current || prefer(entry, current)) {
        best.set(entry.participantIdentity, entry);
      }
    }

    return [...best.values()].sort((a, b) =>
      a.participantIdentity.localeCompare(b.participantIdentity),
    );
  }

  /**
   * Resolves the element a video layer should draw.
   *
   * The sid is exact but does not survive a republish, so a layer that names
   * both falls back to identity plus track name - which is what keeps a
   * composition pointing at "the wide shot" when its camera is restarted.
   */
  resolve(layer: VideoLayer | ScreenShareLayer): HTMLVideoElement | undefined {
    if (layer.trackSid) {
      const bySid = this.entries.get(layer.trackSid);
      if (bySid) {
        return bySid.element;
      }
    }
    if (!layer.participantIdentity) {
      return undefined;
    }

    const wantedName = layer.type === 'video' ? layer.trackName : null;
    for (const entry of this.entries.values()) {
      if (entry.participantIdentity !== layer.participantIdentity) {
        continue;
      }
      if (layer.type === 'screenshare') {
        if (entry.source === Track.Source.ScreenShare) return entry.element;
        continue;
      }
      if (wantedName) {
        if (entry.trackName === wantedName) return entry.element;
        continue;
      }
      if (entry.source === Track.Source.Camera) return entry.element;
    }
    return undefined;
  }

  private readonly onSubscribed = (
    _track: unknown,
    publication: TrackPublication,
    participant: Participant,
  ) => {
    this.add(publication, participant);
    this.emit();
  };

  private readonly onUnsubscribed = (_track: unknown, publication: TrackPublication) => {
    this.remove(publication.trackSid);
    this.emit();
  };

  private readonly onLocalPublished = (publication: TrackPublication) => {
    if (this.room) {
      this.add(publication, this.room.localParticipant);
      this.emit();
    }
  };

  private readonly onLocalUnpublished = (publication: TrackPublication) => {
    this.remove(publication.trackSid);
    this.emit();
  };

  private add(publication: TrackPublication, participant: Participant): void {
    const track = publication.track;
    if (!track || track.kind !== Track.Kind.Video) {
      return;
    }
    if (!DRAWABLE_SOURCES.includes(publication.source)) {
      return;
    }
    if (this.entries.has(publication.trackSid)) {
      return;
    }

    const element = document.createElement('video');
    element.autoplay = true;
    element.playsInline = true;
    // Audio is delivered by the room's own renderer, not by these elements.
    // Leaving them unmuted would double every voice.
    element.muted = true;
    track.attach(element);
    // Autoplay can still be refused; the canvas would then draw a frozen first
    // frame. Nothing here can prompt the user, so it is simply retried.
    void element.play().catch(() => undefined);

    this.entries.set(publication.trackSid, {
      sid: publication.trackSid,
      participantIdentity: participant.identity,
      trackName: publication.trackName,
      source: publication.source,
      element,
    });
  }

  private remove(sid: string): void {
    const entry = this.entries.get(sid);
    if (!entry) return;
    entry.element.srcObject = null;
    this.entries.delete(sid);
  }

  private emit(): void {
    const snapshot = this.list();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}
